import { useCallback, useState } from 'react';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { HTTP_METHOD, CONTENT_TYPE } from '@dealstack/shared/lib/common/dataExchange/common';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';

interface UseDownloadVestingEventsReportParams {
	dealId?: string;
	issuerId?: string;
	investorId?: string;
}

export const useDownloadVestingEventsReport = ({
	dealId,
	issuerId,
	investorId,
}: UseDownloadVestingEventsReportParams) => {
	const [isDownloading, setIsDownloading] = useState(false);

	const onDownload = useCallback(async () => {
		if (!dealId || !issuerId) {
			return;
		}

		setIsDownloading(true);

		try {
			const params = new URLSearchParams({ issuerId });
			if (investorId) {
				params.append('investorId', investorId);
			}
			const endpoint = `${API_PATHS.CAP_VESTING_EVENTS_REPORT_GET}?${params.toString()}`;

			const blob = await makeRequest<Blob>(
				{
					endpoint,
					method: HTTP_METHOD.GET,
					customHeaders: {
						Accept: CONTENT_TYPE.EXCEL,
					},
				},
				{}
			);

			const date = new Date().toISOString().split('T')[0];
			const fileName = `Vesting Events Report - ${date}.xlsx`;

			downloadFileFromBlob(blob, fileName);
		} catch {
			showToastV2({
				state: 'error',
				message: 'Failed to download vesting events report',
			});
		} finally {
			setIsDownloading(false);
		}
	}, [dealId, issuerId, investorId]);

	return {
		onDownload,
		isDownloading,
	};
};
